import { useEffect, useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";

import { Button, Modal, TextArea, TextField } from "../../components/ui/index.ts";
import type { Workstream, WorkstreamInput } from "./types.ts";
import styles from "./workstream-form-modal.module.css";

type FormRow = WorkstreamInput & { key: number };

interface WorkstreamFormModalProps {
  open: boolean;
  mode: "add" | "edit";
  initialWorkstream?: Workstream | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (workstreams: WorkstreamInput[]) => Promise<void>;
}

let nextRowKey = 1;

function emptyRow(): FormRow {
  return { key: nextRowKey++, workstream: "", description: "" };
}

export function WorkstreamFormModal({
  open,
  mode,
  initialWorkstream,
  saving,
  onClose,
  onSubmit,
}: WorkstreamFormModalProps) {
  const [rows, setRows] = useState<FormRow[]>([emptyRow()]);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAttempted(false);
    setRows(
      mode === "edit" && initialWorkstream
        ? [{
            key: nextRowKey++,
            id: initialWorkstream.id,
            workstream: initialWorkstream.workstream,
            description: initialWorkstream.description ?? "",
          }]
        : [emptyRow()],
    );
  }, [initialWorkstream, mode, open]);

  const updateRow = (key: number, field: "workstream" | "description", value: string) => {
    setRows((current) => current.map((row) =>
      row.key === key ? { ...row, [field]: value } : row,
    ));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (rows.some((row) => !row.workstream.trim() || !row.description.trim())) return;

    await onSubmit(rows.map(({ key: _key, ...row }) => ({
      ...row,
      workstream: row.workstream.trim(),
      description: row.description.trim(),
    })));
  };

  const formId = "workstream-form";

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      title={mode === "edit" ? "Edit Work Stream" : "Add Work Stream"}
      size="lg"
      closeOnEscape
      bodyClassName={styles.modalBody}
      footer={
        <div className={styles.footerActions}>
          <Button type="submit" form={formId} disabled={saving}>
            {saving ? "Saving..." : "Submit"}
          </Button>
          <Button variant="light-grey" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className={styles.form} noValidate>
        {rows.map((row, index) => (
          <div className={styles.row} key={row.key}>
            <TextField
              id={`workstream-name-${row.key}`}
              label="Work Stream Name"
              placeholder="Enter work stream name"
              required
              value={row.workstream}
              error={attempted && !row.workstream.trim() ? "Work stream name is required" : undefined}
              onChange={(event) => updateRow(row.key, "workstream", event.target.value)}
            />
            <div className={styles.descriptionField}>
              <TextArea
                id={`workstream-description-${row.key}`}
                label="Work Stream Description"
                placeholder="Enter work stream description"
                rows={2}
                required
                value={row.description}
                error={attempted && !row.description.trim() ? "Work stream description is required" : undefined}
                onChange={(event) => updateRow(row.key, "description", event.target.value)}
              />
              {mode === "add" && index === rows.length - 1 ? (
                <button
                  type="button"
                  className={styles.circleButton}
                  aria-label="Add another work stream"
                  disabled={!row.workstream.trim() || !row.description.trim()}
                  onClick={() => {
                    if (!row.workstream.trim() || !row.description.trim()) {
                      setAttempted(true);
                      return;
                    }
                    setRows((current) => [...current, emptyRow()]);
                  }}
                >
                  <Plus size={22} aria-hidden />
                </button>
              ) : mode === "add" ? (
                <button
                  type="button"
                  className={`${styles.circleButton} ${styles.removeButton}`}
                  aria-label="Remove work stream row"
                  onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                >
                  <X size={20} aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </form>
    </Modal>
  );
}
